import React, { useState } from 'react';
import Button from '../../../components/ui/Button';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatHour = (hour) => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

const CalendarView = ({ 
  selectedDate, 
  onDateChange, 
  assignments, 
  onAssignmentClick,
  viewMode,
  onViewModeChange,
  onDrop
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
  const [dragOverDay, setDragOverDay] = useState(null);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  )?.getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  )?.getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayNamesShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const previousDay = () => {
    const prev = new Date(selectedDate);
    prev?.setDate(prev?.getDate() - 1);
    onDateChange(prev);
  };

  const nextDay = () => {
    const next = new Date(selectedDate);
    next?.setDate(next?.getDate() + 1);
    onDateChange(next);
  };

  const previousWeek = () => {
    const prev = new Date(selectedDate);
    prev?.setDate(prev?.getDate() - 7);
    onDateChange(prev);
  };

  const nextWeek = () => {
    const next = new Date(selectedDate);
    next?.setDate(next?.getDate() + 7);
    onDateChange(next);
  };

  const getWeekDays = (date) => {
    const d = new Date(date);
    const day = d?.getDay();
    const sunday = new Date(d);
    sunday?.setDate(d?.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const wd = new Date(sunday);
      wd?.setDate(sunday?.getDate() + i);
      return wd;
    });
  };

  const isToday = (day) => {
    const today = new Date();
    return (day === today?.getDate() &&
    currentMonth?.getMonth() === today?.getMonth() && currentMonth?.getFullYear() === today?.getFullYear());
  };

  const isSameDay = (dateA, dateB) => {
    return dateA?.getDate() === dateB?.getDate() &&
      dateA?.getMonth() === dateB?.getMonth() &&
      dateA?.getFullYear() === dateB?.getFullYear();
  };

  const isSelectedDate = (day) => {
    return (day === selectedDate?.getDate() &&
    currentMonth?.getMonth() === selectedDate?.getMonth() && currentMonth?.getFullYear() === selectedDate?.getFullYear());
  };

  const getAssignmentsForDay = (day) => {
    return assignments?.filter(assignment => {
      const assignmentDate = new Date(assignment.scheduledDate);
      return (assignmentDate?.getDate() === day &&
      assignmentDate?.getMonth() === currentMonth?.getMonth() && assignmentDate?.getFullYear() === currentMonth?.getFullYear());
    });
  };

  const getAssignmentsForDate = (date) => {
    return assignments?.filter(assignment => {
      const assignmentDate = new Date(assignment.scheduledDate);
      return isSameDay(assignmentDate, date);
    });
  };

  const getAssignmentsForDateAndHour = (date, hour) => {
    return assignments?.filter(assignment => {
      const assignmentDate = new Date(assignment.scheduledDate);
      return isSameDay(assignmentDate, date) && assignmentDate?.getHours() === hour;
    });
  };

  const handleDayClick = (day) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    onDateChange(newDate);
  };

  const handleDragOver = (e, day) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverDay(day);
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  const handleDrop = (e, day) => {
    e?.preventDefault();
    setDragOverDay(null);
    try {
      const technicianData = JSON.parse(e?.dataTransfer?.getData('application/json'));
      const dropDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      if (onDrop) {
        onDrop(technicianData, dropDate);
      }
      onDateChange(dropDate);
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalCells = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
      const day = i - firstDayOfMonth + 1;
      const isValidDay = day > 0 && day <= daysInMonth;
      const dayAssignments = isValidDay ? getAssignmentsForDay(day) : [];
      const isDragOver = dragOverDay === day && isValidDay;

      days?.push(
        <div
          key={i}
          className={`min-h-10 sm:min-h-16 md:min-h-20 p-0.5 sm:p-1 md:p-2 border border-border transition-smooth ${
            isValidDay ? 'bg-card cursor-pointer hover:bg-muted' : 'bg-muted/50'
          } ${isSelectedDate(day) ? 'ring-2 ring-primary' : ''} ${
            isDragOver ? 'bg-primary/10 ring-2 ring-primary ring-dashed' : ''
          }`}
          onClick={() => isValidDay && handleDayClick(day)}
          onDragOver={(e) => isValidDay && handleDragOver(e, day)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => isValidDay && handleDrop(e, day)}
        >
          {isValidDay && (
            <>
              <div className={`text-xs font-medium mb-0.5 ${
                isToday(day) 
                  ? 'w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs' 
                  : 'text-foreground'
              }`}>
                {day}
              </div>
              {isDragOver && (
                <div className="hidden sm:block text-xs text-primary font-medium text-center py-1">
                  Drop
                </div>
              )}
              <div className="space-y-0.5">
                {/* Mobile: show dot indicators only */}
                <div className="flex flex-wrap gap-0.5 sm:hidden">
                  {dayAssignments?.slice(0, 3)?.map((assignment, idx) => (
                    <div
                      key={idx}
                      className={`w-1.5 h-1.5 rounded-full ${
                        assignment?.status === 'scheduled' ? 'bg-primary' :
                        assignment?.status === 'in-progress' ? 'bg-warning' : 'bg-success'
                      }`}
                      onClick={(e) => {
                        e?.stopPropagation();
                        onAssignmentClick(assignment);
                      }}
                    />
                  ))}
                </div>
                {/* Tablet+: show ticket labels */}
                {dayAssignments?.slice(0, 2)?.map((assignment, idx) => (
                  <div
                    key={idx}
                    className={`hidden sm:block text-xs p-0.5 sm:p-1 rounded truncate cursor-pointer transition-smooth ${
                      assignment?.status === 'scheduled' ? 'bg-primary/10 text-primary hover:bg-primary/20' :
                      assignment?.status === 'in-progress' ? 'bg-warning/10 text-warning hover:bg-warning/20' : 'bg-success/10 text-success hover:bg-success/20'
                    }`}
                    onClick={(e) => {
                      e?.stopPropagation();
                      onAssignmentClick(assignment);
                    }}
                  >
                    {assignment?.ticketId}
                  </div>
                ))}
                {dayAssignments?.length > 2 && (
                  <div className="hidden sm:block text-xs text-muted-foreground">
                    +{dayAssignments?.length - 2}
                  </div>
                )}
                {dayAssignments?.length > 3 && (
                  <div className="sm:hidden text-xs text-muted-foreground leading-none">
                    +{dayAssignments?.length - 3}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    return days;
  };

  const renderDayView = () => {
    const isDateToday = isSameDay(selectedDate, new Date());
    const currentHour = new Date()?.getHours();

    return (
      <div className="overflow-y-auto max-h-[500px] sm:max-h-[600px]">
        <div className="min-w-0">
          {/* Day header */}
          <div className="sticky top-0 z-10 bg-card border-b border-border px-3 sm:px-4 py-2 sm:py-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base sm:text-lg font-bold ${
                isDateToday ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
              }`}>
                {selectedDate?.getDate()}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {fullDayNames?.[selectedDate?.getDay()]}
                </p>
                <p className="text-xs text-muted-foreground">
                  {monthNames?.[selectedDate?.getMonth()]} {selectedDate?.getFullYear()}
                </p>
              </div>
            </div>
          </div>

          {/* Hourly slots */}
          <div className="divide-y divide-border">
            {HOURS?.map((hour) => {
              const hourAssignments = getAssignmentsForDateAndHour(selectedDate, hour);
              const isCurrentHour = isDateToday && hour === currentHour;

              return (
                <div
                  key={hour}
                  className={`flex min-h-12 sm:min-h-14 ${
                    isCurrentHour ? 'bg-primary/5' : 'hover:bg-muted/50'
                  } transition-smooth`}
                >
                  <div className="w-12 sm:w-16 flex-shrink-0 px-1 sm:px-2 py-2 text-right">
                    <span className={`text-xs ${
                      isCurrentHour ? 'text-primary font-semibold' : 'text-muted-foreground'
                    }`}>
                      {formatHour(hour)}
                    </span>
                  </div>
                  <div className="flex-1 px-1 sm:px-2 py-1 border-l border-border">
                    {isCurrentHour && (
                      <div className="w-full h-0.5 bg-primary mb-1 rounded" />
                    )}
                    <div className="space-y-1">
                      {hourAssignments?.map((assignment, idx) => (
                        <div
                          key={idx}
                          className={`text-xs px-1.5 sm:px-2 py-1 sm:py-1.5 rounded cursor-pointer transition-smooth ${
                            assignment?.status === 'scheduled' ? 'bg-primary/10 text-primary hover:bg-primary/20' :
                            assignment?.status === 'in-progress' ? 'bg-warning/10 text-warning hover:bg-warning/20' : 'bg-success/10 text-success hover:bg-success/20'
                          }`}
                          onClick={() => onAssignmentClick(assignment)}
                        >
                          <span className="font-medium">{assignment?.ticketId}</span>
                          {assignment?.scheduledTime && (
                            <span className="ml-1 sm:ml-2 opacity-75">{assignment?.scheduledTime}</span>
                          )}
                          {assignment?.location && (
                            <span className="ml-1 sm:ml-2 opacity-60 truncate block">{assignment?.location}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(selectedDate);
    const today = new Date();
    const currentHour = today?.getHours();

    return (
      <div className="overflow-auto max-h-[500px] sm:max-h-[600px]">
        {/* Mobile week view: compact with abbreviated names */}
        <div className="min-w-[320px] sm:min-w-[560px] md:min-w-[640px]">
          {/* Week header */}
          <div className="sticky top-0 z-10 bg-card border-b border-border grid grid-cols-8">
            <div className="w-10 sm:w-14 md:w-16 flex-shrink-0" />
            {weekDays?.map((day, idx) => {
              const isCurrentDay = isSameDay(day, today);
              const isSelected = isSameDay(day, selectedDate);
              return (
                <div
                  key={idx}
                  className={`py-1.5 sm:py-2 px-0.5 sm:px-1 text-center cursor-pointer hover:bg-muted/50 transition-smooth ${
                    isSelected ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => onDateChange(new Date(day))}
                >
                  {/* Mobile: single letter */}
                  <p className="text-xs text-muted-foreground sm:hidden">{dayNamesShort?.[day?.getDay()]}</p>
                  {/* Tablet+: 3-letter abbreviation */}
                  <p className="hidden sm:block text-xs text-muted-foreground">{dayNames?.[day?.getDay()]}</p>
                  <div className={`mx-auto mt-0.5 sm:mt-1 w-5 h-5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
                    isCurrentDay ? 'bg-primary text-primary-foreground' :
                    isSelected ? 'bg-primary/20 text-primary' : 'text-foreground'
                  }`}>
                    {day?.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hourly rows */}
          <div className="divide-y divide-border">
            {HOURS?.map((hour) => {
              return (
                <div key={hour} className="grid grid-cols-8 min-h-10 sm:min-h-14">
                  {/* Time label */}
                  <div className="w-10 sm:w-14 md:w-16 flex-shrink-0 px-1 sm:px-2 py-1 sm:py-2 text-right">
                    <span className="text-xs text-muted-foreground leading-tight">{formatHour(hour)}</span>
                  </div>
                  {/* Day columns */}
                  {weekDays?.map((day, dayIdx) => {
                    const hourAssignments = getAssignmentsForDateAndHour(day, hour);
                    const isCurrentHourToday = isSameDay(day, today) && hour === currentHour;

                    return (
                      <div
                        key={dayIdx}
                        className={`border-l border-border px-0.5 sm:px-1 py-0.5 sm:py-1 ${
                          isCurrentHourToday ? 'bg-primary/5' : 'hover:bg-muted/30'
                        } transition-smooth`}
                      >
                        {isCurrentHourToday && (
                          <div className="w-full h-0.5 bg-primary mb-0.5 sm:mb-1 rounded" />
                        )}
                        <div className="space-y-0.5">
                          {hourAssignments?.map((assignment, idx) => (
                            <div
                              key={idx}
                              className={`text-xs px-0.5 sm:px-1 py-0.5 rounded cursor-pointer truncate transition-smooth ${
                                assignment?.status === 'scheduled' ? 'bg-primary/10 text-primary hover:bg-primary/20' :
                                assignment?.status === 'in-progress' ? 'bg-warning/10 text-warning hover:bg-warning/20' : 'bg-success/10 text-success hover:bg-success/20'
                              }`}
                              onClick={() => onAssignmentClick(assignment)}
                              title={`${assignment?.ticketId} - ${assignment?.location || ''}`}
                            >
                              {/* Mobile: dot only */}
                              <span className="sm:hidden inline-block w-1.5 h-1.5 rounded-full bg-current" />
                              {/* Tablet+: ticket ID */}
                              <span className="hidden sm:inline">{assignment?.ticketId}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Header navigation label
  const getHeaderLabel = () => {
    if (viewMode === 'day') {
      return `${monthNames?.[selectedDate?.getMonth()]} ${selectedDate?.getDate()}, ${selectedDate?.getFullYear()}`;
    }
    if (viewMode === 'week') {
      const weekDays = getWeekDays(selectedDate);
      const start = weekDays?.[0];
      const end = weekDays?.[6];
      if (start?.getMonth() === end?.getMonth()) {
        return `${monthNames?.[start?.getMonth()]} ${start?.getDate()} – ${end?.getDate()}, ${start?.getFullYear()}`;
      }
      return `${monthNames?.[start?.getMonth()]} ${start?.getDate()} – ${monthNames?.[end?.getMonth()]} ${end?.getDate()}, ${end?.getFullYear()}`;
    }
    return `${monthNames?.[currentMonth?.getMonth()]} ${currentMonth?.getFullYear()}`;
  };

  const getHeaderLabelShort = () => {
    if (viewMode === 'day') {
      return `${monthNames?.[selectedDate?.getMonth()]?.substring(0, 3)} ${selectedDate?.getDate()}`;
    }
    if (viewMode === 'week') {
      const weekDays = getWeekDays(selectedDate);
      const start = weekDays?.[0];
      const end = weekDays?.[6];
      return `${monthNames?.[start?.getMonth()]?.substring(0, 3)} ${start?.getDate()}–${end?.getDate()}`;
    }
    return `${monthNames?.[currentMonth?.getMonth()]?.substring(0, 3)} ${currentMonth?.getFullYear()}`;
  };

  const handlePrev = () => {
    if (viewMode === 'day') previousDay();
    else if (viewMode === 'week') previousWeek();
    else previousMonth();
  };

  const handleNext = () => {
    if (viewMode === 'day') nextDay();
    else if (viewMode === 'week') nextWeek();
    else nextMonth();
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    onDateChange(today);
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-2 sm:p-3 md:p-4 border-b border-border">
        {/* Header row: label + nav */}
        <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4 gap-2">
          {/* Short label on mobile, full on larger screens */}
          <h2 className="text-sm sm:text-base md:text-xl font-heading font-semibold text-foreground truncate">
            <span className="sm:hidden">{getHeaderLabelShort()}</span>
            <span className="hidden sm:inline">{getHeaderLabel()}</span>
          </h2>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              onClick={handlePrev}
              className="h-7 w-7 sm:h-9 sm:w-auto p-0 sm:px-3"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleToday}
              className="h-7 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              onClick={handleNext}
              className="h-7 w-7 sm:h-9 sm:w-auto p-0 sm:px-3"
            />
          </div>
        </div>

        {/* View mode buttons */}
        <div className="flex items-center gap-1">
          <Button
            variant={viewMode === 'day' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('day')}
            className="flex-1 sm:flex-none h-7 sm:h-9 text-xs sm:text-sm px-2 sm:px-3"
          >
            Day
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('week')}
            className="flex-1 sm:flex-none h-7 sm:h-9 text-xs sm:text-sm px-2 sm:px-3"
          >
            Week
          </Button>
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('month')}
            className="flex-1 sm:flex-none h-7 sm:h-9 text-xs sm:text-sm px-2 sm:px-3"
          >
            Month
          </Button>
        </div>

        {/* Drag hint - hidden on mobile to save space */}
        <div className="hidden sm:flex mt-2 sm:mt-3 items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded px-3 py-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span>Drag a technician card onto a calendar day to schedule an assignment</span>
        </div>
      </div>

      {viewMode === 'month' && (
        <>
          <div className="grid grid-cols-7 border-b border-border">
            {dayNames?.map((day, idx) => (
              <div
                key={day}
                className="p-1 sm:p-2 text-center bg-muted/50"
              >
                {/* Mobile: single letter */}
                <span className="sm:hidden text-xs font-medium text-muted-foreground">{dayNamesShort?.[idx]}</span>
                {/* Tablet+: 3-letter */}
                <span className="hidden sm:inline text-xs md:text-sm font-medium text-muted-foreground">{day}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {renderCalendarDays()}
          </div>
        </>
      )}

      {viewMode === 'day' && renderDayView()}

      {viewMode === 'week' && renderWeekView()}
    </div>
  );
};

export default CalendarView;